import React, { useRef, useState, useEffect, useMemo } from "react";
import { ImageOff, ChevronLeft, ChevronRight, X } from "lucide-react";
import { NoDataView } from "../NoDataView";

// Display ad "imageUrl"s aren't always a real image - some are interactive HTML5
// creatives (e.g. Amazon Display units) served from a URL with no file extension.
// Treat anything without a recognized image extension as interactive and iframe it.
const STATIC_IMAGE_EXTENSION_REGEX = /\.(jpe?g|png|gif|webp|svg|bmp|avif)(\?|#|$)/i;
const isStaticImageUrl = (url: string) => STATIC_IMAGE_EXTENSION_REGEX.test(url);

interface IRawVideo {
    videoId: number;
    videoEmbedCode: string;
    videoName: string;
}

// Shared by the `videos` and `ottVideos` fields - both carry the same shape and
// need the same YouTube-embed-code parsing to derive a playable URL + thumbnail.
function transformVideos(videos: IRawVideo[] | undefined | null): ICreativeGalleryItem[] {
    if (!videos || !Array.isArray(videos)) return [];

    return videos
        .filter((video) => video.videoEmbedCode && video.videoEmbedCode.trim() !== '') // Filter out videos without valid embed code
        .map((video): ICreativeGalleryItem => {
            // Extract YouTube embed URL from embed code
            let embedUrl: string | null = null;
            let thumbnailUrl: string | null = null;
            const fullEmbedCode = video.videoEmbedCode;

            const youtubeMatch = video.videoEmbedCode.match(/src="https:\/\/www\.youtube\.com\/embed\/([^"]+)"/);
            if (youtubeMatch) {
                const videoId = youtubeMatch[1];
                embedUrl = `https://www.youtube.com/embed/${videoId}`;
                thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
            }

            return {
                adId: video.videoId,
                imageUrl: thumbnailUrl,
                videoUrl: embedUrl,
                videoThumbnail: thumbnailUrl,
                videoEmbedCode: fullEmbedCode, // Store the full embed code
                type: 'video' as const,
            };
        })
        .filter((video, index, array) =>
            // Remove duplicates based on videoUrl or videoId
            array.findIndex((v) =>
                (v.videoUrl && v.videoUrl === video.videoUrl) ||
                v.adId === video.adId
            ) === index
        );
}

// Renders one video-shaped creative (used by both the Video and OTT sections):
// prefers the full embed code's own attributes, falling back to a plain iframe
// around videoUrl, and finally a placeholder if neither is present.
function VideoCreativeCard({ item }: { item: ICreativeGalleryItem }) {
    if (item.videoEmbedCode) {
        // Parse the embed code to extract attributes
        const embedCode = item.videoEmbedCode;
        const srcMatch = embedCode.match(/src="([^"]+)"/);
        const widthMatch = embedCode.match(/width="([^"]+)"/);
        const heightMatch = embedCode.match(/height="([^"]+)"/);
        const titleMatch = embedCode.match(/title="([^"]+)"/);
        const frameborderMatch = embedCode.match(/frameborder="([^"]+)"/);
        const allowMatch = embedCode.match(/allow="([^"]+)"/);

        const src = srcMatch ? srcMatch[1] : '';
        // Don't autoplay in carousel to avoid multiple videos playing

        return (
            <div className="aspect-video">
                <iframe
                    src={src}
                    width={widthMatch ? widthMatch[1] : "100%"}
                    height={heightMatch ? heightMatch[1] : "100%"}
                    title={titleMatch ? titleMatch[1] : `Video Creative ${item.adId}`}
                    frameBorder={frameborderMatch ? frameborderMatch[1] : "0"}
                    allow={allowMatch ? allowMatch[1] : "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"}
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                    className="rounded-xl w-full h-full object-cover border border-gray-300 shadow-sm"
                />
            </div>
        );
    }

    if (item.videoUrl) {
        return (
            <div className="aspect-video">
                <iframe
                    src={item.videoUrl}
                    className="rounded-xl w-full h-full object-cover border border-gray-300 shadow-sm"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                    title={`Video Creative ${item.adId}`}
                />
            </div>
        );
    }

    return (
        <div className="w-full h-full flex items-center justify-center border border-gray-300 rounded-xl bg-gray-50">
            <ImageOff className="h-8 w-8 text-gray-400" />
        </div>
    );
}

export interface ICreativeGalleryItem {
    adId: number | string;
    imageUrl: string | null;
    videoUrl?: string | null;
    videoEmbedCode?: string | null;
    imageHeight?: number;
    imageWidth?: number;
    videoThumbnail?: string | null;
    duration?: number;
    type?: 'image' | 'video';
}

export interface ICreativeGalleryData {
    total: number;
    items: ICreativeGalleryItem[];
    displayItems?: ICreativeGalleryItem[];
    videos?: IRawVideo[];
    ottVideos?: IRawVideo[];
    warnings?: {
        id: string;
        type: string;
        description: string;
    }[];
}

export function CreativeGallery({ 
    data, 
    metadata
}: { 
    data: ICreativeGalleryData; 
    metadata?: any;
}) {
    const transformedData = useMemo(() => {
        // Handle new backend structure with separate videos array
        if (data?.videos && Array.isArray(data.videos)) {
            // Transform paid social items (ensure they have type: 'image')
            const paidSocialItems: ICreativeGalleryItem[] = (data.items || [])
                .filter((item: any) => item.imageUrl && item.imageUrl.trim() !== '') // Filter out items without valid imageUrl
                .map((item: any) => ({
                    ...item,
                    type: 'image' as const,
                    imageUrl: item.imageUrl || null,
                    videoUrl: null,
                    videoThumbnail: null,
                }));

            // Transform video items
            const videoItems: ICreativeGalleryItem[] = transformVideos(data.videos);

            // Combine both datasets
            const allItems = [...paidSocialItems, ...videoItems];
            
            return {
                total: allItems.length,
                items: allItems,
                warnings: data.warnings || []
            };
        }

        // Return data for paid social creatives or already processed data
        return {
            ...data,
            items: (data.items || [])
                .filter((item: any) => item.imageUrl && item.imageUrl.trim() !== '') // Filter out items without valid imageUrl
                .map((item: any) => ({
                    ...item,
                    type: item.type || 'image' as const,
                }))
        };
    }, [data]);

    // Display ad items are already in their own field, shaped like `items` - keep them
    // separate from the paid social / video split above rather than merging them in.
    const displayItems: ICreativeGalleryItem[] = useMemo(() => {
        return (data?.displayItems || [])
            .filter((item: any) => item.imageUrl && item.imageUrl.trim() !== '')
            .map((item: any) => ({
                ...item,
                type: 'image' as const,
            }));
    }, [data?.displayItems]);

    // OTT videos are the same shape as `videos` but their own section - keep them
    // separate from the paid social / video split above rather than merging them in.
    const ottItems: ICreativeGalleryItem[] = useMemo(() => transformVideos(data?.ottVideos), [data?.ottVideos]);

    // Determine what to render based on metadata
    const includesVideo = metadata?.includesVideo || false;
    const includesPaidSocial = metadata?.includesPaidSocial !== false; // default to true

    // Separate paid social and video items
    const paidSocialItems = transformedData?.items?.filter(item => item.type !== 'video') || [];
    const videoItems = transformedData?.items?.filter(item => item.type === 'video') || [];
    const hasPaidSocial = includesPaidSocial && paidSocialItems.length > 0;
    const hasVideo = includesVideo && videoItems.length > 0;
    const hasDisplay = displayItems.length > 0;
    const hasOtt = ottItems.length > 0;

    // Separate scroll states for each gallery
    const paidSocialScrollRef = useRef<HTMLDivElement>(null);
    const videoScrollRef = useRef<HTMLDivElement>(null);
    const displayScrollRef = useRef<HTMLDivElement>(null);
    const ottScrollRef = useRef<HTMLDivElement>(null);
    const [paidSocialCanScrollLeft, setPaidSocialCanScrollLeft] = useState(false);
    const [paidSocialCanScrollRight, setPaidSocialCanScrollRight] = useState(false);
    const [videoCanScrollLeft, setVideoCanScrollLeft] = useState(false);
    const [videoCanScrollRight, setVideoCanScrollRight] = useState(false);
    const [displayCanScrollLeft, setDisplayCanScrollLeft] = useState(false);
    const [displayCanScrollRight, setDisplayCanScrollRight] = useState(false);
    const [ottCanScrollLeft, setOttCanScrollLeft] = useState(false);
    const [ottCanScrollRight, setOttCanScrollRight] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState<ICreativeGalleryItem | null>(null);

    const checkPaidSocialScroll = () => {
        if (paidSocialScrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = paidSocialScrollRef.current;
            setPaidSocialCanScrollLeft(scrollLeft > 0);
            setPaidSocialCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
        }
    };

    const checkVideoScroll = () => {
        if (videoScrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = videoScrollRef.current;
            setVideoCanScrollLeft(scrollLeft > 0);
            setVideoCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
        }
    };

    const checkDisplayScroll = () => {
        if (displayScrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = displayScrollRef.current;
            setDisplayCanScrollLeft(scrollLeft > 0);
            setDisplayCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
        }
    };

    const checkOttScroll = () => {
        if (ottScrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = ottScrollRef.current;
            setOttCanScrollLeft(scrollLeft > 0);
            setOttCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
        }
    };

    useEffect(() => {
        checkPaidSocialScroll();
        const el = paidSocialScrollRef.current;
        el?.addEventListener("scroll", checkPaidSocialScroll);
        window.addEventListener("resize", checkPaidSocialScroll);
        return () => {
            el?.removeEventListener("scroll", checkPaidSocialScroll);
            window.removeEventListener("resize", checkPaidSocialScroll);
        };
    }, [paidSocialItems.length]);

    useEffect(() => {
        checkVideoScroll();
        const el = videoScrollRef.current;
        el?.addEventListener("scroll", checkVideoScroll);
        window.addEventListener("resize", checkVideoScroll);
        return () => {
            el?.removeEventListener("scroll", checkVideoScroll);
            window.removeEventListener("resize", checkVideoScroll);
        };
    }, [videoItems.length]);

    useEffect(() => {
        checkDisplayScroll();
        const el = displayScrollRef.current;
        el?.addEventListener("scroll", checkDisplayScroll);
        window.addEventListener("resize", checkDisplayScroll);
        return () => {
            el?.removeEventListener("scroll", checkDisplayScroll);
            window.removeEventListener("resize", checkDisplayScroll);
        };
    }, [displayItems.length]);

    useEffect(() => {
        checkOttScroll();
        const el = ottScrollRef.current;
        el?.addEventListener("scroll", checkOttScroll);
        window.addEventListener("resize", checkOttScroll);
        return () => {
            el?.removeEventListener("scroll", checkOttScroll);
            window.removeEventListener("resize", checkOttScroll);
        };
    }, [ottItems.length]);

    const scrollPaidSocial = (direction: "left" | "right") => {
        if (paidSocialScrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = paidSocialScrollRef.current;
            const cardWidth = 400;
            const gap = 24;

            if (direction === "left") {
                const newPosition = Math.max(0, scrollLeft - cardWidth - gap);
                paidSocialScrollRef.current.scrollTo({ left: newPosition, behavior: "smooth" });
            } else {
                const maxScroll = scrollWidth - clientWidth;
                const newPosition = Math.min(maxScroll, scrollLeft + cardWidth + gap);
                paidSocialScrollRef.current.scrollTo({ left: newPosition, behavior: "smooth" });
            }
        }
    };

    const scrollVideo = (direction: "left" | "right") => {
        if (videoScrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = videoScrollRef.current;
            const cardWidth = 400;
            const gap = 24;

            if (direction === "left") {
                const newPosition = Math.max(0, scrollLeft - cardWidth - gap);
                videoScrollRef.current.scrollTo({ left: newPosition, behavior: "smooth" });
            } else {
                const maxScroll = scrollWidth - clientWidth;
                const newPosition = Math.min(maxScroll, scrollLeft + cardWidth + gap);
                videoScrollRef.current.scrollTo({ left: newPosition, behavior: "smooth" });
            }
        }
    };

    const scrollDisplay = (direction: "left" | "right") => {
        if (displayScrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = displayScrollRef.current;
            const cardWidth = 400;
            const gap = 24;

            if (direction === "left") {
                const newPosition = Math.max(0, scrollLeft - cardWidth - gap);
                displayScrollRef.current.scrollTo({ left: newPosition, behavior: "smooth" });
            } else {
                const maxScroll = scrollWidth - clientWidth;
                const newPosition = Math.min(maxScroll, scrollLeft + cardWidth + gap);
                displayScrollRef.current.scrollTo({ left: newPosition, behavior: "smooth" });
            }
        }
    };

    const scrollOtt = (direction: "left" | "right") => {
        if (ottScrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = ottScrollRef.current;
            const cardWidth = 400;
            const gap = 24;

            if (direction === "left") {
                const newPosition = Math.max(0, scrollLeft - cardWidth - gap);
                ottScrollRef.current.scrollTo({ left: newPosition, behavior: "smooth" });
            } else {
                const maxScroll = scrollWidth - clientWidth;
                const newPosition = Math.min(maxScroll, scrollLeft + cardWidth + gap);
                ottScrollRef.current.scrollTo({ left: newPosition, behavior: "smooth" });
            }
        }
    };

    if (!paidSocialItems.length && !videoItems.length && !displayItems.length && !ottItems.length) {
        return <NoDataView />;
    }

    return (
        <div className="relative w-full mt-4 p-4">
            {/* Paid Social Gallery */}
            {hasPaidSocial && (
                <div className="mb-8">
                    <h2 className="font-semibold text-widget-title ml-4 mb-4" style={{ fontFamily: 'Roboto, sans-serif', fontSize: '16px' }}>
                        Paid Social
                    </h2>
                    <div className="relative">
                        {paidSocialItems.length > 3 && paidSocialCanScrollLeft && (
                            <button
                                onClick={() => scrollPaidSocial("left")}
                                className="absolute left-6 top-1/2 -translate-y-1/2 z-10 bg-teal-600 hover:bg-teal-700 shadow-md rounded-full p-2 transition-opacity"
                            >
                                <ChevronLeft className="h-5 w-5 text-white" />
                            </button>
                        )}

                        <div ref={paidSocialScrollRef} className="w-full overflow-x-auto scrollbar-hide px-4">
                            <div
                                className="grid gap-6"
                                style={{
                                    gridTemplateColumns: `repeat(${paidSocialItems.length}, 400px)`,
                                }}
                            >
                                {paidSocialItems.map((item: ICreativeGalleryItem) => {
                                    const mediaUrl = item.imageUrl;
                                     
                                    return (
                                        <div key={item.adId} className="overflow-hidden h-auto relative">
                                            {mediaUrl ? (
                                                <img 
                                                    src={mediaUrl} 
                                                    alt={`Creative ${item.adId}`} 
                                                    className="rounded-xl w-full h-auto object-cover border border-gray-300 shadow-sm" 
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center border border-gray-300 rounded-xl bg-gray-50">
                                                    <ImageOff className="h-8 w-8 text-gray-400" />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {paidSocialItems.length > 3 && paidSocialCanScrollRight && (
                            <button
                                onClick={() => scrollPaidSocial("right")}
                                className="absolute right-6 top-1/2 -translate-y-1/2 z-10 bg-teal-600 hover:bg-teal-700 shadow-md rounded-full p-2 transition-opacity"
                            >
                                <ChevronRight className="h-5 w-5 text-white" />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Video Gallery */}
            {hasVideo && (
                <div className="mb-8">
                    <h2 className="font-semibold text-widget-title ml-4 mb-4" style={{ fontFamily: 'Roboto, sans-serif', fontSize: '16px' }}>
                        Video
                    </h2>
                    <div className="relative">
                        {videoItems.length > 3 && videoCanScrollLeft && (
                            <button
                                onClick={() => scrollVideo("left")}
                                className="absolute left-6 top-1/2 -translate-y-1/2 z-10 bg-teal-600 hover:bg-teal-700 shadow-md rounded-full p-2 transition-opacity"
                            >
                                <ChevronLeft className="h-5 w-5 text-white" />
                            </button>
                        )}

                        <div ref={videoScrollRef} className="w-full overflow-x-auto scrollbar-hide px-4">
                            <div
                                className="grid gap-6"
                                style={{
                                    gridTemplateColumns: `repeat(${videoItems.length}, 400px)`,
                                }}
                            >
                                {videoItems.map((item: ICreativeGalleryItem) => (
                                    <div key={item.adId} className="overflow-hidden h-auto relative">
                                        <VideoCreativeCard item={item} />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {videoItems.length > 3 && videoCanScrollRight && (
                            <button
                                onClick={() => scrollVideo("right")}
                                className="absolute right-6 top-1/2 -translate-y-1/2 z-10 bg-teal-600 hover:bg-teal-700 shadow-md rounded-full p-2 transition-opacity"
                            >
                                <ChevronRight className="h-5 w-5 text-white" />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* OTT Gallery */}
            {hasOtt && (
                <div className="mb-8">
                    <h2 className="font-semibold text-widget-title ml-4 mb-4" style={{ fontFamily: 'Roboto, sans-serif', fontSize: '16px' }}>
                        OTT
                    </h2>
                    <div className="relative">
                        {ottItems.length > 3 && ottCanScrollLeft && (
                            <button
                                onClick={() => scrollOtt("left")}
                                className="absolute left-6 top-1/2 -translate-y-1/2 z-10 bg-teal-600 hover:bg-teal-700 shadow-md rounded-full p-2 transition-opacity"
                            >
                                <ChevronLeft className="h-5 w-5 text-white" />
                            </button>
                        )}

                        <div ref={ottScrollRef} className="w-full overflow-x-auto scrollbar-hide px-4">
                            <div
                                className="grid gap-6"
                                style={{
                                    gridTemplateColumns: `repeat(${ottItems.length}, 400px)`,
                                }}
                            >
                                {ottItems.map((item: ICreativeGalleryItem) => (
                                    <div key={item.adId} className="overflow-hidden h-auto relative">
                                        <VideoCreativeCard item={item} />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {ottItems.length > 3 && ottCanScrollRight && (
                            <button
                                onClick={() => scrollOtt("right")}
                                className="absolute right-6 top-1/2 -translate-y-1/2 z-10 bg-teal-600 hover:bg-teal-700 shadow-md rounded-full p-2 transition-opacity"
                            >
                                <ChevronRight className="h-5 w-5 text-white" />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Amazon Display Gallery */}
            {hasDisplay && (
                <div className="mb-8">
                    <h2 className="font-semibold text-widget-title ml-4 mb-4" style={{ fontFamily: 'Roboto, sans-serif', fontSize: '16px' }}>
                        Amazon Display
                    </h2>
                    <div className="relative">
                        {displayItems.length > 3 && displayCanScrollLeft && (
                            <button
                                onClick={() => scrollDisplay("left")}
                                className="absolute left-6 top-1/2 -translate-y-1/2 z-10 bg-teal-600 hover:bg-teal-700 shadow-md rounded-full p-2 transition-opacity"
                            >
                                <ChevronLeft className="h-5 w-5 text-white" />
                            </button>
                        )}

                        <div ref={displayScrollRef} className="w-full overflow-x-auto scrollbar-hide px-4">
                            <div
                                className="grid gap-6"
                                style={{
                                    gridTemplateColumns: `repeat(${displayItems.length}, 400px)`,
                                }}
                            >
                                {displayItems.map((item: ICreativeGalleryItem) => {
                                    const mediaUrl = item.imageUrl;
                                    const isInteractive = !!mediaUrl && !isStaticImageUrl(mediaUrl);
                                    const creativeWidth = item.imageWidth || 300;
                                    const creativeHeight = item.imageHeight || 250;

                                    return (
                                        <div key={item.adId} className="overflow-hidden h-auto relative">
                                            {!mediaUrl ? (
                                                <div className="w-full h-full flex items-center justify-center border border-gray-300 rounded-xl bg-gray-50">
                                                    <ImageOff className="h-8 w-8 text-gray-400" />
                                                </div>
                                            ) : isInteractive ? (
                                                // Interactive creative (e.g. Amazon Display HTML5 unit) - has its own fixed
                                                // pixel layout, so it's iframed at its native size rather than stretched.
                                                <div
                                                    className="flex items-center justify-center rounded-xl border border-gray-300 shadow-sm bg-white overflow-hidden mx-auto"
                                                    style={{ width: creativeWidth, maxWidth: '100%' }}
                                                >
                                                    <iframe
                                                        src={mediaUrl}
                                                        width={creativeWidth}
                                                        height={creativeHeight}
                                                        title={`Creative ${item.adId}`}
                                                        frameBorder="0"
                                                        scrolling="no"
                                                        className="border-0 max-w-full"
                                                    />
                                                </div>
                                            ) : (
                                                <img
                                                    src={mediaUrl}
                                                    alt={`Creative ${item.adId}`}
                                                    className="rounded-xl w-full h-auto object-cover border border-gray-300 shadow-sm"
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {displayItems.length > 3 && displayCanScrollRight && (
                            <button
                                onClick={() => scrollDisplay("right")}
                                className="absolute right-6 top-1/2 -translate-y-1/2 z-10 bg-teal-600 hover:bg-teal-700 shadow-md rounded-full p-2 transition-opacity"
                            >
                                <ChevronRight className="h-5 w-5 text-white" />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Video Modal */}
            {selectedVideo && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="relative bg-white rounded-lg max-w-4xl max-h-full w-full">
                        <button
                            onClick={() => setSelectedVideo(null)}
                            className="absolute -top-10 right-0 text-white hover:text-gray-300"
                        >
                            <X className="h-6 w-6" />
                        </button>
                        <div className="aspect-video">
                            {selectedVideo.videoEmbedCode ? (
                                (() => {
                                    // Parse the embed code to extract attributes
                                    const embedCode = selectedVideo.videoEmbedCode;
                                    const srcMatch = embedCode.match(/src="([^"]+)"/);
                                    const widthMatch = embedCode.match(/width="([^"]+)"/);
                                    const heightMatch = embedCode.match(/height="([^"]+)"/);
                                    const titleMatch = embedCode.match(/title="([^"]+)"/);
                                    const frameborderMatch = embedCode.match(/frameborder="([^"]+)"/);
                                    const allowMatch = embedCode.match(/allow="([^"]+)"/);
                                    
                                    const src = srcMatch ? srcMatch[1] : '';
                                    const finalSrc = src.includes('?') ? `${src}&autoplay=1&rel=0&modestbranding=1` : `${src}?autoplay=1&rel=0&modestbranding=1`;
                                    
                                    return (
                                        <iframe
                                            src={finalSrc}
                                            width={widthMatch ? widthMatch[1] : "100%"}
                                            height={heightMatch ? heightMatch[1] : "100%"}
                                            title={titleMatch ? titleMatch[1] : "Video Player"}
                                            frameBorder={frameborderMatch ? frameborderMatch[1] : "0"}
                                            allow={allowMatch ? allowMatch[1] : "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"}
                                            allowFullScreen
                                            referrerPolicy="strict-origin-when-cross-origin"
                                            className="w-full h-full rounded-lg"
                                        />
                                    );
                                })()
                            ) : (
                                <iframe
                                    src={`${selectedVideo.videoUrl}?autoplay=1&rel=0&modestbranding=1`}
                                    className="w-full h-full rounded-lg"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                    referrerPolicy="strict-origin-when-cross-origin"
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
